// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { getCore } from 'cvat-core-wrapper';
import { LogType } from 'cvat-logger';
import config from 'config';
import { platformInfo } from 'utils/platform-checker';

const core = getCore();
const { CONTROLS_LOGS_INTERVAL } = config;

const classFilter = [
    'ant-btn',
    'ant-radio-button-wrapper',
    'ant-radio-wrapper',
    'ant-radio-input',
    'ant-checkbox-wrapper',
    'ant-checkbox-input',
    'ant-select-selection-item',
    'ant-select-item-option-content',
    'ant-select-selector',
    'ant-input',
    'cvat-transcript-review-segment',
    'cvat-transcript-review-stage-option',
    'cvat-transcript-segment',
];
const parentClassFilter = [
    'ant-btn',
    'ant-radio-button-wrapper',
    'ant-radio-wrapper',
    'ant-checkbox-wrapper',
    'ant-select-selector',
    'cvat-transcript-review-segment',
    'cvat-transcript-segment',
];

function getRouteJobContext(): { jobId?: number; taskId?: number } {
    const { pathname } = window.location;
    const match = pathname.match(/\/tasks\/(\d+)\/jobs\/(\d+)/);
    if (match) {
        return {
            taskId: Number(match[1]),
            jobId: Number(match[2]),
        };
    }
    const jobMatch = pathname.match(/\/jobs\/(\d+)/);
    if (jobMatch) {
        return {
            jobId: Number(jobMatch[1]),
        };
    }
    return {};
}

class EventRecorder {
    #savingInterval: number | null;
    public constructor() {
        this.#savingInterval = null;
        core.logger.log(LogType.loadTool, {
            location: window.location.pathname + window.location.search,
            platform: platformInfo(),
        });
    }

    public log(event: MouseEvent): void {
        const { target } = event;
        if (!target) {
            return;
        }

        let element = (target as HTMLElement);
        let toRecord = this.isEventToBeRecorded(element, classFilter);

        const { jobId, taskId } = getRouteJobContext();
        const logData: Record<string, any> = {
            text: element.innerText,
            classes: this.filterClassName(element.className),
            location: window.location.pathname + window.location.search,
        };
        if (jobId) {
            logData.job_id = jobId;
        }
        if (taskId) {
            logData.task_id = taskId;
        }

        if (!toRecord && element.parentElement) {
            element = element.parentElement;
            toRecord = this.isEventToBeRecorded(element, parentClassFilter);
            logData.classes = this.filterClassName(element.className);
        }

        if (toRecord) {
            core.logger.log(LogType.clickElement, logData, false);
        }
    }

    public initSave(): void {
        if (this.#savingInterval) return;
        this.#savingInterval = setInterval(() => {
            core.logger.save();
        }, CONTROLS_LOGS_INTERVAL) as unknown as number;
    }

    private filterClassName(cls: string): string {
        if (typeof cls === 'string') {
            return cls.split(' ').filter((_cls: string) => _cls.startsWith('cvat')).join(' ');
        }

        return '';
    }

    private isEventToBeRecorded(node: HTMLElement, filter: string[]): boolean {
        return filter.some((cssClass: string) => node.classList.contains(cssClass));
    }
}

export default new EventRecorder();
